import { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router';

import AdminLayout from './app/(admin)/layout';
import AuthLayout from './app/(auth)/layout';
import HelpLayout from './app/(help)/layout';
import MainLayout from './app/(main)/layout';

const HomePage = lazy(() => import('./app/page'));

const SignInPage = lazy(() => import('./app/(auth)/signin/page'));
const ForgotPasswordPage = lazy(() => import('./app/(auth)/forgot-password/page'));
const ResetPasswordPage = lazy(() => import('./app/(auth)/reset-password/page'));
const AcceptInvitationPage = lazy(() => import('./app/(auth)/accept-invitation/page'));

const HelpPage = lazy(() => import('./app/(main)/help/page'));
const ProfilePage = lazy(() => import('./app/(main)/profile/page'));
const LabsListPage = lazy(() => import('./app/(main)/labs/page'));
const LabCreatePage = lazy(() => import('./app/(main)/labs/create/page'));
const LabPage = lazy(() => import('./app/(main)/labs/[labId]/page'));
const BatchJobPage = lazy(() => import('./app/(main)/labs/[labId]/batch-job/[jobId]/page'));
const OmicsRunPage = lazy(() => import('./app/(main)/labs/[labId]/omics-run/[omicsRunId]/page'));
const LabRunPage = lazy(() => import('./app/(main)/labs/[labId]/run/[labRunId]/page'));
const RunPipelinePage = lazy(() => import('./app/(main)/labs/[labId]/run-pipeline/[pipelineId]/page'));
const RunBatchPipelinePage = lazy(() => import('./app/(main)/labs/[labId]/run-batch-pipeline/[pipelineId]/page'));
const RunWorkflowPage = lazy(() => import('./app/(main)/labs/[labId]/run-workflow/[workflowId]/page'));
const SeqeraRunPage = lazy(() => import('./app/(main)/labs/[labId]/seqera-run/[seqeraRunId]/page'));
const SampleSheetPage = lazy(() => import('./app/(main)/labs/[labId]/sample-sheet/page'));
const OrgsPage = lazy(() => import('./app/(main)/orgs/page'));
const OrgPage = lazy(() => import('./app/(main)/orgs/[orgId]/page'));
const EditUserPage = lazy(() => import('./app/(main)/orgs/[orgId]/edit-user/[userId]/page'));

const AdminPage = lazy(() => import('./app/(admin)/admin/page'));
const AdminHelpPage = lazy(() => import('./app/(admin)/admin/help/page'));
const AdminHelpTabPage = lazy(() => import('./app/(admin)/admin/help/[tabId]/page'));
const AdminOrgsPage = lazy(() => import('./app/(admin)/admin/orgs/page'));
const AdminOrgCreatePage = lazy(() => import('./app/(admin)/admin/orgs/create/page'));
const AdminOrgPage = lazy(() => import('./app/(admin)/admin/orgs/[orgId]/page'));
const AdminEditUserPage = lazy(() => import('./app/(admin)/admin/orgs/[orgId]/edit-user/[userId]/page'));
const AdminLabPage = lazy(() => import('./app/(admin)/admin/orgs/[orgId]/labs/[labId]/page'));

const UsageDashboardPage = lazy(() => import('./app/(main)/usage-dashboard/page'));
const AdminUsageDashboardPage = lazy(() => import('./app/(admin)/admin/usage-dashboard/page'));

export function AppRoutes() {
  return (
    <Suspense>
      <Routes>
        <Route element={<AuthLayout />}>
          <Route path="/signin" element={<SignInPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
        </Route>

        <Route element={<HelpLayout />}>
          <Route path="/help" element={<HelpPage />} />
          <Route path="/admin/help" element={<AdminHelpPage />} />
          <Route path="/admin/help/:tabId" element={<AdminHelpTabPage />} />
        </Route>

        <Route element={<MainLayout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/labs" element={<LabsListPage />} />
          <Route path="/labs/create" element={<LabCreatePage />} />
          <Route path="/labs/:labId" element={<LabPage />} />
          <Route path="/labs/:labId/batch-job/:jobId" element={<BatchJobPage />} />
          <Route path="/labs/:labId/omics-run/:omicsRunId" element={<OmicsRunPage />} />
          <Route path="/labs/:labId/run/:labRunId" element={<LabRunPage />} />
          <Route path="/labs/:labId/run-pipeline/:pipelineId" element={<RunPipelinePage />} />
          <Route path="/labs/:labId/run-batch-pipeline/:pipelineId" element={<RunBatchPipelinePage />} />
          <Route path="/labs/:labId/run-workflow/:workflowId" element={<RunWorkflowPage />} />
          <Route path="/labs/:labId/seqera-run/:seqeraRunId" element={<SeqeraRunPage />} />
          <Route path="/labs/:labId/sample-sheet" element={<SampleSheetPage />} />
          <Route path="/orgs" element={<OrgsPage />} />
          <Route path="/orgs/:orgId" element={<OrgPage />} />
          <Route path="/orgs/:orgId/edit-user/:userId" element={<EditUserPage />} />
          <Route path="/usage-dashboard" element={<UsageDashboardPage />} />
        </Route>

        <Route element={<AdminLayout />}>
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/admin/orgs" element={<AdminOrgsPage />} />
          <Route path="/admin/orgs/create" element={<AdminOrgCreatePage />} />
          <Route path="/admin/orgs/:orgId" element={<AdminOrgPage />} />
          <Route path="/admin/orgs/:orgId/edit-user/:userId" element={<AdminEditUserPage />} />
          <Route path="/admin/orgs/:orgId/labs/:labId" element={<AdminLabPage />} />
          <Route path="/admin/usage-dashboard" element={<AdminUsageDashboardPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
